import {
  AddressBook,
  convertAddressBookToCreate,
  convertAddressBookToUpdate,
} from "../Models/AddressBook";
import {
  Location,
  convertLocationToCreate,
  convertLocationToUpdate,
} from "../Models/Location";
import { AddressBookService as RegistryAddressBookService } from "../services/registry/addressBook";
import { LocationsService as RegistryLocationsService } from "../services/registry/locations";

const registryAddressBookService = new RegistryAddressBookService();
const registryLocationsService = new RegistryLocationsService();

/**
 * Hook for managing the saving of an AddressBook to the "registry".
 * It handles both creation and editing of the AddressBook and its associated Location.
 */
export const useRegistryAddressBook = () => {
  /**
   * Creates a new registry addressBook along with its location.
   * @param data - The AddressBook data to create.
   * @returns The created AddressBook with its new registryId.
   */
  const createRegistryAddressBook = async (
    data: AddressBook
  ): Promise<AddressBook> => {
    const locationToCreate = convertLocationToCreate(data.location as Location);
    const locationRes = await registryLocationsService.create(locationToCreate);

    const addressBookToCreate = convertAddressBookToCreate(data);
    addressBookToCreate.locationId = locationRes.id;

    const addressBookRes = await registryAddressBookService.create(
      addressBookToCreate
    );

    // Return the updated data structure
    return {
      ...data,
      registryId: addressBookRes.id,
      location: {
        ...data.location,
        registryId: locationRes.id,
      },
    } as AddressBook;
  };

  /**
   * Edits an existing registry addressBook and its location.
   * @param data - The AddressBook data to update.
   * @returns The updated AddressBook data.
   */
  const editRegistryAddressBook = async (
    data: AddressBook
  ): Promise<AddressBook> => {
    const location = { ...data.location, id: data.location?.registryId };
    const locationToUpdate = convertLocationToUpdate(location as Location);
    await registryLocationsService.edit(locationToUpdate);

    const addressBook = { ...data, id: data.registryId };
    const addressBookToUpdate = convertAddressBookToUpdate(
      addressBook as AddressBook
    );
    addressBookToUpdate.locationId = location.id as string;
    await registryAddressBookService.edit(addressBookToUpdate);

    // Return the original data structure, as IDs don't change on edit
    return data;
  };

  /**
   * Saves an AddressBook to the registry, deciding whether to create or edit.
   * @param data - The AddressBook data to save.
   * @returns The updated AddressBook with registry IDs.
   */
  const saveRegistryAddressBook = async (
    data: AddressBook
  ): Promise<AddressBook | undefined> => {
    if (!data?.location) {
      console.error("Location is missing, cannot save to registry.");
      return;
    }

    // The caller will handle loading state, errors, and success feedback.
    return data.registryId
      ? editRegistryAddressBook(data)
      : createRegistryAddressBook(data);
  };

  return { saveRegistryAddressBook };
};
